import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  Signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonEstablishAmendment } from '@app/features/amendments/button-establish-amendment/button-establish-amendment';
import { nameContractDownloads } from '@app/features/contracts/contract-file-name';
import {
  Contract,
  ContractListItem,
  toContractListItem,
} from '@app/features/contracts/contract.model';
import { DocumentsMetadata } from '@app/features/documents/document-metadata.model';
import { CdTDocument } from '@app/features/documents/document.model';
import { LayoutService } from '@app/nav/layout/layout.service';
import { CopyLocationButton } from '@app/shared/buttons/copy-location-button/copy-location-button';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import { XIconButton } from '@app/shared/buttons/x-icon-button/x-icon-button';
import { toLocalDateString } from '@app/shared/date-utils';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { ButtonContractHistory } from '../button-contract-history/button-contract-history';
import { ButtonContractPdf } from '../button-contract-pdf/button-contract-pdf';
import { ContractActionButtons } from '../contract-action-buttons/contract-action-buttons';
import { ContractStatusComponent } from '../contract-status/contract-status';
import { DocumentService } from './../../documents/document-service';
import { ContractService } from './../contract-service';
import { EditContract } from './../contract.model';
import { AssignmentFormValue } from './embauche-tabs-form/assignment-form/assignment-form';
import { ContractFormValue } from './embauche-tabs-form/contract-form/contract-form';
import { DocumentsFormValue } from './embauche-tabs-form/documents-form/documents-form';
import { EmbaucheTabsForm } from './embauche-tabs-form/embauche-tabs-form';
import { EmployeeFormValue } from './embauche-tabs-form/employee-form/employee-form';
import { SchedulesFormValue } from './embauche-tabs-form/schedules-form/schedules-form';
import { EmbaucheView } from './embauche-view/embauche-view';
import { SignatureSendFailure } from '@app/features/contracts/signature/signature-send-failure/signature-send-failure';

export type EditMode = 'draft' | 'validation';
@Component({
  selector: 'app-embauche-dialog',
  imports: [
    CommonModule,
    EmbaucheTabsForm,
    EmbaucheView,
    MatButtonModule,
    MatDialogContent,
    MatDialogActions,
    SaveButton,
    ContractStatusComponent,
    MatButtonToggleModule,
    MatTooltipModule,
    XIconButton,
    CopyLocationButton,
    ContractActionButtons,
    ButtonContractHistory,
    ButtonContractPdf,
    SignatureSendFailure,
    ButtonEstablishAmendment,
  ],
  templateUrl: './embauche-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./embauche-dialog.scss'],
})
export class EmbaucheDialog extends RxjsDirective implements OnInit, AfterViewInit {
  readonly dialogRef = inject(MatDialogRef<EmbaucheDialog>, { optional: true });
  readonly injectedData = inject<Contract | undefined>(MAT_DIALOG_DATA, { optional: true });
  /** When used outside of a dialog, pass the contract via this input */
  readonly contract = input<Contract | undefined>();
  /** Emits the saved/created/updated contract when used outside a dialog */
  readonly saved = output<Contract>();
  /** Emits when the user cancels and no MatDialogRef is available */
  readonly canceled = output<void>();
  readonly contractService = inject(ContractService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly documentService = inject(DocumentService);
  readonly layoutService = inject(LayoutService);
  contactFrom?: EmployeeFormValue;

  @ViewChild(EmbaucheTabsForm) private embaucheTabsForm!: EmbaucheTabsForm;
  @ViewChild(SaveButton) private saveButton!: SaveButton;

  contactSig?: Signal<EmployeeFormValue>;
  contractSig?: Signal<ContractFormValue>;
  schedulesSig?: Signal<SchedulesFormValue>;
  assignmentSig!: Signal<AssignmentFormValue>;
  documentsSig!: Signal<DocumentsFormValue>;

  /** Dernière version du contrat reçue du serveur, mise à jour par les actions du contrat */
  protected readonly refreshedContract = signal<Contract | undefined>(undefined);

  inputData = computed(() => this.injectedData || this.contract());
  /** Contrat courant au format liste, requis par les boutons d'action */
  readonly contractListItem = computed<ContractListItem | undefined>(() => {
    const contract = this.refreshedContract() ?? this.inputData();
    return contract?.id ? toContractListItem(contract) : undefined;
  });
  showPreview = true;
  validationMode = !this.canDraftMode;

  get canDraftMode(): boolean {
    const contract = this.inputData();
    if (contract?.deleted) {
      return false;
    }
    return contract?.id === undefined || contract?.status === 'DRAFT';
  }

  get isReadOnly(): boolean {
    const contract = this.inputData();
    // Un contrat supprimé logiquement reste consultable, mais n'est plus modifiable.
    if (contract?.deleted) {
      return true;
    }
    return contract?.status !== undefined && contract.status !== 'DRAFT';
  }

  ngOnInit(): void {
    this.register(
      this.contractService.updated$.subscribe((contract) => {
        if (contract.id && contract.id === this.inputData()?.id) {
          this.refreshedContract.set(contract);
        }
      }),
    );
    this.cdr.detectChanges();
  }

  /** Recharge le contrat pour rafraîchir les boutons d'action et le statut */
  refreshContract(): void {
    const contractId = this.inputData()?.id;
    if (!contractId) {
      return;
    }
    this.register(
      this.contractService.findOne(contractId).subscribe(() => {
        // Rien à faire, la mise à jour est propagée par contractService.updated$
      }),
    );
  }

  onContractUpdated(contract: Contract): void {
    this.refreshedContract.set(contract);
  }

  /** Le contrat affiché vient d'être supprimé : il n'y a plus rien à éditer, on ferme. */
  onContractDeleted(): void {
    this.onCancel();
  }

  ngAfterViewInit(): void {
    this.contactSig = this.embaucheTabsForm.contactSig;
    this.contractSig = this.embaucheTabsForm.contractSig;
    this.schedulesSig = this.embaucheTabsForm.schedulesSig;
    this.assignmentSig = this.embaucheTabsForm.assignmentSig;
    this.documentsSig = this.embaucheTabsForm.documentsSig;
    this.cdr.detectChanges();
  }

  onCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.canceled.emit();
  }

  get allTabsFormsValid(): boolean {
    return [
      (() => {
        const contract = this.inputData();
        // Aucun enregistrement possible sur un contrat supprimé.
        if (contract?.deleted) {
          return false;
        }
        return contract && contract.status ? contract.status === 'DRAFT' : true;
      })(),
      this.embaucheTabsForm?.employeeForm?.isValid,
      this.embaucheTabsForm?.contractForm?.isValid,
      this.embaucheTabsForm?.schedulesForm?.isValid,
      this.embaucheTabsForm?.assignmentForm?.form.valid,
      this.embaucheTabsForm?.documentsForm?.form.valid,
    ].every((isValid: boolean | undefined) => isValid === true);
  }

  onSave(): void {
    if (this.inputData()?.id) {
      this.update();
    } else {
      this.create();
    }
    this.layoutService.previewDocument(null);
  }

  create(): void {
    if (!this.allTabsFormsValid) {
      console.error('Formulaire invalide, création annulée.');
      //return;
    }
    this.saveButton.startSaving();
    const editContract: EditContract = this.toEditContract();
    this.register(
      this.contractService.create(editContract).subscribe({
        next: (createdContract) => {
          this.saveButton.stopSaving(true);
          console.log('Contrat créé avec succès:', createdContract);
          if (this.dialogRef) {
            setTimeout(() => this.dialogRef!.close(createdContract), 600);
          } else {
            setTimeout(() => this.saved.emit(createdContract), 600);
          }
        },
        error: (error) => {
          this.saveButton.stopSaving(false);
          console.error('Erreur lors de la création du contrat:', error);
        },
      }),
    );
  }

  toEditContract(): EditContract {
    const firstName = this.contactSig?.().firstname ?? '';
    const lastName = this.contactSig?.().lastname ?? '';
    const contractDto = {
      id: this.inputData()?.id ?? undefined,
      company: {
        id: this.contractSig?.().companyId ?? '',
      },
      employee: {
        id: this.contactSig?.().id ?? null,
        birthName: this.contactSig?.().birthName ?? null,
        firstname: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        lastname: lastName.toUpperCase(),
        email: this.contactSig?.().email ?? null,
        additionalAddress: this.contactSig?.().additionalAddress ?? null,
        phoneNumber: this.contactSig?.().phoneNumber ?? null,
        title: this.contactSig?.().title ?? 'Monsieur',
        birthdate: this.contactSig?.().birthdate ?? null,
        birthplace: this.contactSig?.().birthplace ?? null,
        birthCountry: this.contactSig?.().birthCountry ?? null,
        socialSecurityNumber: this.contactSig?.().socialSecurityNumber ?? null,
        address: this.contactSig?.().address ?? null,
        postalCode: this.contactSig?.().postalCode ?? null,
        city: this.contactSig?.().city ?? null,
        nationality: this.contactSig?.().nationality ?? null,
      },
      contract: {
        contractType: this.contractSig!().contractType!,
        startDate: toLocalDateString(this.contractSig!().startDate)!,
        endDate: toLocalDateString(this.contractSig!().endDate),
        endOfFirstTrialPeriodDate: toLocalDateString(
          this.contractSig!().endOfFirstTrialPeriodDate,
        )!,
        replacementCivility: this.contractSig!().replacement?.civility ?? null,
        replacementFirstName: this.contractSig!().replacement?.firstName ?? null,
        replacementLastName: this.contractSig!().replacement?.lastName ?? null,
        replacementReason: this.contractSig!().replacement?.reason ?? null,
        replacementQualificationLevel: this.contractSig!().replacement?.qualificationLevel ?? null,
        replacementJobId: this.contractSig!().replacement?.jobId ?? null,
        jobId: this.contractSig!().jobId ?? null,
        qualificationLevel: this.contractSig!().qualificationLevel ?? null,
        grossHourlySalary: this.contractSig!().grossHourlySalary ?? null,
        previousContractId: this.contractSig!().previousContractId ?? null,
        companyCar: this.contractSig!().companyCar ?? false,
      },
      schedule: {
        collectiveSchedules: this.schedulesSig?.().collectiveSchedules ?? false,
        schedules: this.schedulesSig?.().schedules ?? [],
      },
      residencies: this.assignmentSig!().residencies ?? null,
      assignmentType: this.assignmentSig!().assignmentType ?? null,
      documents: this.documentsSig!().documents.map((doc) => ({
        tag: doc.tag,
        id: doc.id,
      })),
      documentsMetadata: {
        residentPermitExpirationDate: toLocalDateString(
          this.documentsSig!().metadata?.residentPermitExpirationDate,
        ),
        drivingLicenseExpirationDate: toLocalDateString(
          this.documentsSig!().metadata?.drivingLicenseExpirationDate,
        ),
      },
    };
    return contractDto;
  }

  update(): void {
    if (!this.allTabsFormsValid) {
      console.error('Formulaire invalide, mise à jour annulée.');
      return;
    }
    this.saveButton.startSaving();
    console.log('Enregistrement du contrat en cours...');

    const contract: EditContract = this.toEditContract();
    contract.id = this.inputData()?.id;
    this.contractService.update(contract).subscribe({
      next: (updatedContract) => {
        this.saveButton.stopSaving(true);
        console.log('Contrat mis à jour avec succès:', updatedContract);
        if (this.dialogRef) {
          setTimeout(() => this.dialogRef!.close(updatedContract), 600);
        } else {
          setTimeout(() => this.saved.emit(updatedContract), 600);
        }
      },
      error: (error) => {
        this.saveButton.stopSaving(false);
        console.error('Erreur lors de la mise à jour du contrat:', error);
      },
    });
    return;
  }

  openDocument($event: CdTDocument) {
    if (this.injectedData) {
      this.documentService.preview($event);
    } else {
      this.layoutService.previewDocument($event);
    }
  }

  onDocumentUploaded($event: CdTDocument) {
    this.nameContractDownloads($event);
    this.openDocument($event);
    if (this.inputData()?.id) {
      this.register(
        this.contractService
          .addDocument(this.inputData()!.id!, { id: $event.id, tag: $event.tag })
          .subscribe({
            next: (updatedContract) => {
              console.log('Document ajouté avec succès:', updatedContract);
            },
            error: (error) => {
              console.error("Erreur lors de l'ajout du document:", error);
            },
          }),
      );
    }
  }

  /**
   * Une pièce de contrat déposée se télécharge sous le nom du disque réseau, d'après le contrat
   * tel qu'il est saisi, et renumérote au besoin celles de même nature
   * (RG-contrat-nom-de-telechargement).
   */
  private nameContractDownloads(uploaded: CdTDocument): void {
    const documents = this.documentsSig?.().documents ?? [];
    const contract = this.contractSig?.();
    nameContractDownloads(
      documents.includes(uploaded) ? documents : [...documents, uploaded],
      contract && {
        contractType: contract.contractType,
        startDate: toLocalDateString(contract.startDate),
        endDate: toLocalDateString(contract.endDate),
      },
    );
  }

  onDocumentsMetadataChanged($event: DocumentsMetadata) {
    if (this.inputData()?.id) {
      $event.residentPermitExpirationDate = toLocalDateString($event.residentPermitExpirationDate);
      $event.drivingLicenseExpirationDate = toLocalDateString($event.drivingLicenseExpirationDate);

      this.contractService.updateDocumentsMetadata(this.inputData()!.id!, $event).subscribe({
        next: (updatedContract) => {
          console.log('Metadata des documents mis à jour avec succès:', updatedContract);
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour des metadata des documents:', error);
        },
      });
    }
  }

  onValidationModeChange(validationMode: boolean) {
    this.validationMode = validationMode;
  }
}
